import React, { lazy, Suspense } from 'react';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';

import LoadingScreen from 'components/LoadingScreen/LoadingScreen';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import LogoOnlyLayout from 'layouts/LogoOnlyLayout';
import ModuleNew from 'pages/dashboard/ModuleNew';
import GuestGuard from 'guards/GuestGuard';
import RoleGuard from 'guards/RoleGuard';
import AuthGuard from 'guards/AuthGuard';
import { APP_ROLES } from 'utils/roles';

// eslint-disable-next-line react/display-name
const Loadable = Component => props => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { pathname } = useLocation();
    const isDashboard = pathname.includes('/dashboard');

    return (
        <Suspense
            fallback={
                <LoadingScreen
                    sx={{
                        ...(!isDashboard && {
                            top: 0,
                            left: 0,
                            width: 1,
                            zIndex: 9999,
                            position: 'fixed',
                        }),
                    }}
                />
            }
        >
            <Component {...props} />
        </Suspense>
    );
};

// IMPORT COMPONENTS

// Authentication
const Login = Loadable(lazy(() => import('pages/auth/Login/Login')));
const Register = Loadable(lazy(() => import('pages/auth/Register/Register')));
const ResetPassword = Loadable(lazy(() => import('pages/auth/ResetPassword/ResetPassword')));
const VerifyEmailAndCode = Loadable(lazy(() => import('pages/auth/VerifyEmailAndCode/VerifyEmailAndCode')));
const UserManagement = Loadable(lazy(() => import('pages/auth/UserManagement/UserManagement')));

// Dashboard
const Courses = Loadable(lazy(() => import('pages/dashboard/Courses')));
const Course = Loadable(lazy(() => import('pages/dashboard/Course')));
const LessonNew = Loadable(lazy(() => import('pages/dashboard/LessonNew')));
const Scores = Loadable(lazy(() => import('pages/dashboard/Scores')));

// Management
const User = Loadable(lazy(() => import('pages/dashboard/User')));
const Groups = Loadable(lazy(() => import('pages/dashboard/Groups')));
const Admin = Loadable(lazy(() => import('pages/dashboard/Admin')));
const AdminGroups = Loadable(lazy(() => import('pages/dashboard/AdminGroups')));
const AdminUsers = Loadable(lazy(() => import('pages/dashboard/AdminUsers')));
const RoleManagement = Loadable(lazy(() => import('pages/dashboard/RoleManagement')));
const Inventory = Loadable(lazy(() => import('pages/dashboard/Inventory')));
const BestScores = Loadable(lazy(() => import('pages/dashboard/BestScores')));
const ChatInsights = Loadable(lazy(() => import('pages/dashboard/ChatInsights')));

const GroupContent = Loadable(lazy(() => import('pages/dashboard/GroupContent')));

// Misc
const Page404 = Loadable(lazy(() => import('pages/Page404')));
const Page500 = Loadable(lazy(() => import('pages/Page500')));

export default function Router() {
    return useRoutes([
        {
            path: 'auth',
            children: [
                {
                    path: 'login',
                    element: (
                        <GuestGuard>
                            <Login />
                        </GuestGuard>
                    ),
                },
                {
                    path: 'register',
                    element: (
                        <GuestGuard>
                            <Register />
                        </GuestGuard>
                    ),
                },
                {
                    path: 'reset-password',
                    element: (
                        <GuestGuard>
                            <ResetPassword />
                        </GuestGuard>
                    ),
                },
                {
                    path: 'verify',
                    element: (
                        <GuestGuard>
                            <VerifyEmailAndCode />
                        </GuestGuard>
                    ),
                },
                { path: 'user-management', element: <UserManagement /> },
            ],
        },
        {
            path: 'dashboard',
            element: (
                <AuthGuard>
                    <DashboardLayout />
                </AuthGuard>
            ),
            children: [
                { index: true, element: <Navigate to='/dashboard/courses' replace /> },
                {
                    path: 'scores',
                    element: <Scores />,
                },
                {
                    path: 'courses',
                    element: <Courses />,
                },
                {
                    path: 'courses/public/:module',
                    element: <ModuleNew />,
                },
                {
                    path: 'courses/public/:module/:lesson',
                    children: [
                        { index: true, element: <LessonNew /> },
                        { path: ':topic', element: <LessonNew /> },
                    ],
                },
                {
                    path: 'courses/:course',
                    element: <Course />,
                },
                {
                    path: 'courses/:course/:module',
                    element: <ModuleNew />,
                },
                {
                    path: 'courses/:course/:module/:lesson',
                    children: [
                        { index: true, element: <LessonNew /> },
                        { path: ':topic', element: <LessonNew /> },
                    ],
                },
                {
                    path: 'user',
                    element: <User />,
                },
                {
                    path: 'groups',
                    element: (
                        <RoleGuard accessibleRoles={[APP_ROLES.SUPER_ADMIN, APP_ROLES.ADMIN, APP_ROLES.DESTYTOJAS]}>
                            <Groups />
                        </RoleGuard>
                    ),
                },
                {
                    path: 'admin',
                    element: (
                        <RoleGuard accessibleRoles={[APP_ROLES.SUPER_ADMIN, APP_ROLES.ADMIN]}>
                            <Admin />
                        </RoleGuard>
                    ),
                },
                {
                    path: 'admin/roles',
                    element: (
                        <RoleGuard accessibleRoles={[APP_ROLES.SUPER_ADMIN]}>
                            <RoleManagement />
                        </RoleGuard>
                    ),
                },
                {
                    path: 'manage-groups',
                    element: (
                        <RoleGuard accessibleRoles={[APP_ROLES.SUPER_ADMIN, APP_ROLES.ADMIN]}>
                            <AdminGroups />
                        </RoleGuard>
                    ),
                },
                {
                    path: 'manage-users',
                    element: (
                        <RoleGuard accessibleRoles={[APP_ROLES.SUPER_ADMIN, APP_ROLES.ADMIN]}>
                            <AdminUsers />
                        </RoleGuard>
                    ),
                },
                {
                    path: 'inventory',
                    element: (
                        <RoleGuard accessibleRoles={[APP_ROLES.SUPER_ADMIN]}>
                            <Inventory />
                        </RoleGuard>
                    ),
                },
                {
                    path: 'best_scores',
                    element: (
                        <RoleGuard accessibleRoles={[APP_ROLES.SUPER_ADMIN]}>
                            <BestScores />
                        </RoleGuard>
                    ),
                },
                {
                    path: 'admin/chat-insights',
                    element: (
                        <RoleGuard accessibleRoles={[APP_ROLES.SUPER_ADMIN]}>
                            <ChatInsights />
                        </RoleGuard>
                    ),
                },
                {
                    path: 'inventory/form',
                    element: (
                        <RoleGuard accessibleRoles={[APP_ROLES.SUPER_ADMIN]}>
                            <Inventory />
                        </RoleGuard>
                    ),
                },
                {
                    path: 'groups/:group/content',
                    element: (
                        <RoleGuard accessibleRoles={[APP_ROLES.SUPER_ADMIN, APP_ROLES.ADMIN, APP_ROLES.DESTYTOJAS]}>
                            <GroupContent />
                        </RoleGuard>
                    ),
                },
            ],
        },
        {
            path: '/',
            element: (
                <AuthGuard>
                    <DashboardLayout />
                </AuthGuard>
            ),
            children: [{ index: true, element: <Navigate to='/dashboard' replace /> }],
        },
        {
            path: '*',
            element: <LogoOnlyLayout />,
            children: [
                // { path: 'maintenance', element: <Maintenance /> },
                { path: '404', element: <Page404 /> },
                { path: '500', element: <Page500 /> },
                { path: '*', element: <Navigate to='/404' replace /> },
            ],
        },
        { path: '*', element: <Navigate to='/404' replace /> },
    ]);
}
